import DataOps from './DataOps';

type Method = 'GET' | 'POST';

function getCandidateBases(): string[] {
    const configuredBase = DataOps.getCloudFunctionBase()?.replace(/\/$/, '') || '';
    const defaultBase = 'https://us-central1-snapcamp-62d3c.cloudfunctions.net';
    const explicitStripeBase = ((globalThis as any)?.STRIPE_CLOUD_FN_BASE || '').toString().replace(/\/$/, '');

    // Do not include browser origin fallback here; static web hosts (e.g. Vercel)
    // will return "Cannot POST /stripeCreateOrGetCustomer" and mask the real backend issue.
    const candidates = [explicitStripeBase, configuredBase, defaultBase].filter(Boolean);
    return Array.from(new Set(candidates));
}

async function parseJsonSafe(response: Response): Promise<any> {
    const text = await response.text();
    if (!text) return {};
    try {
        return JSON.parse(text);
    } catch {
        return { error: text };
    }
}

async function http(method: Method, path: string, body?: any): Promise<any> {
    const bases = getCandidateBases();
    let lastError = 'Unknown error';

    for (const base of bases) {
        const url = `${base}/${path}`;
        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: method === 'POST' ? JSON.stringify(body || {}) : undefined,
            });

            if (response.ok) {
                return parseJsonSafe(response);
            }

            const payload = await parseJsonSafe(response);
            const responseMessage = payload?.error || payload?.message || `HTTP ${response.status}`;
            lastError = `${responseMessage} (${response.status})`;
            if (response.status === 403) {
                lastError = 'Stripe Cloud Function access denied (403). Check function IAM/public access and Stripe secret config.';
            } else if (response.status === 404) {
                lastError = 'Stripe Cloud Function endpoint not found (404). Check deployed function names and base URL.';
            }
            continue;
        } catch (e: any) {
            lastError = e?.message || String(e);
            continue;
        }
    }

    throw new Error(lastError);
}

export function getStripePublishableKey(): string {
    const globalKey = (globalThis as any)?.STRIPE_PUBLISHABLE_KEY;
    const envKey = (globalThis as any)?.process?.env?.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY;
    return globalKey || envKey || '';
}

export async function getOrCreateCustomer(params: { userId: string; email?: string; name?: string }): Promise<{ customerId: string }> {
    return http('POST', 'stripeCreateOrGetCustomer', params);
}

export async function createSetupIntent(customerId: string): Promise<{ clientSecret: string }> {
    return http('POST', 'stripeCreateSetupIntent', { customerId, type: 'card' });
}

export async function createBankSetupIntent(customerId: string): Promise<{ clientSecret: string }> {
    return http('POST', 'stripeCreateSetupIntent', { customerId, type: 'us_bank_account' });
}

export async function listPaymentMethods(customerId: string, type?: 'card' | 'us_bank_account'): Promise<{ paymentMethods: any[] }> {
    const encodedCustomerId = encodeURIComponent(customerId);
    const encodedType = type ? `&type=${encodeURIComponent(type)}` : '';
    const bases = getCandidateBases();
    let lastError = 'Unknown error';

    for (const base of bases) {
        const url = `${base}/stripeListPaymentMethods?customerId=${encodedCustomerId}${encodedType}`;
        try {
            const response = await fetch(url);
            if (response.ok) {
                return parseJsonSafe(response);
            }

            const payload = await parseJsonSafe(response);
            const responseMessage = payload?.error || payload?.message || `HTTP ${response.status}`;
            lastError = `${responseMessage} (${response.status})`;
            if (response.status === 403) {
                lastError = 'Stripe Cloud Function access denied (403). Check function IAM/public access and Stripe secret config.';
            } else if (response.status === 404) {
                lastError = 'Stripe Cloud Function endpoint not found (404). Check deployed function names and base URL.';
            }
            continue;
        } catch (e: any) {
            lastError = e?.message || String(e);
            continue;
        }
    }

    throw new Error(lastError);
}

export async function detachPaymentMethod(paymentMethodId: string): Promise<void> {
    await http('POST', 'stripeDetachPaymentMethod', { paymentMethodId });
}
